import {
  FingerprintCollectionResult,
  FingerprintCollectionStatus,
  FingerprintData,
} from "@/core/dtos/fingerprint-data";
import {
  collectFingerprint,
  generateSessionFingerprintId,
  isFingerprintingSupported,
} from "@/utils/fingerprint-util";
import { useCallback, useEffect, useState } from "react";

interface UseFingerprintOptions {
  autoCollect?: boolean;
  persistInSession?: boolean;
  sessionStorageKey?: string;
  onCollectionComplete?: (result: FingerprintCollectionResult) => void;
  onCollectionError?: (error: string) => void;
}

interface UseFingerprintReturn {
  fingerprintData: FingerprintData | null;
  status: FingerprintCollectionStatus;
  isSupported: boolean;
  isCollecting: boolean;
  error: string | null;
  collectionTimeMs: number | null;
  sessionId: string;
  // Manually trigger fingerprint collection
  collectFingerprint: () => Promise<FingerprintCollectionResult>;
  clearFingerprint: () => void;
  getFingerprintHash: () => string | null;
}

const DEFAULT_SESSION_STORAGE_KEY = "tmm_fingerprint";
const SESSION_ID_STORAGE_KEY = "tmm_fingerprint_session_id";

export const useFingerprint = (
  options: UseFingerprintOptions = {},
): UseFingerprintReturn => {
  const {
    autoCollect = true,
    persistInSession = true,
    sessionStorageKey = DEFAULT_SESSION_STORAGE_KEY,
    onCollectionComplete,
    onCollectionError,
  } = options;

  const [fingerprintData, setFingerprintData] =
    useState<FingerprintData | null>(null);
  const [status, setStatus] = useState<FingerprintCollectionStatus>(
    FingerprintCollectionStatus.NOT_STARTED,
  );
  const [error, setError] = useState<string | null>(null);
  const [collectionTimeMs, setCollectionTimeMs] = useState<number | null>(null);
  const [sessionId] = useState<string>(() => {
    const existingSessionId = persistInSession
      ? sessionStorage.getItem(SESSION_ID_STORAGE_KEY)
      : null;

    if (existingSessionId) {
      return existingSessionId;
    }

    const newSessionId = generateSessionFingerprintId();
    if (persistInSession) {
      sessionStorage.setItem(SESSION_ID_STORAGE_KEY, newSessionId);
    }

    return newSessionId;
  });

  const isSupported = isFingerprintingSupported();
  const isCollecting = status === FingerprintCollectionStatus.IN_PROGRESS;
  const loadFromSession = useCallback((): FingerprintData | null => {
    if (!persistInSession) return null;

    try {
      const stored = sessionStorage.getItem(sessionStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.hash && parsed.collectedAt) {
          return {
            ...parsed,
            collectedAt: new Date(parsed.collectedAt),
          };
        }
      }
    } catch (error) {
      console.warn("Failed to load fingerprint from session storage:", error);
    }

    return null;
  }, [persistInSession, sessionStorageKey]);

  const saveToSession = useCallback(
    (data: FingerprintData): void => {
      if (!persistInSession) return;

      try {
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(data));
      } catch (error) {
        console.warn("Failed to save fingerprint to session storage:", error);
      }
    },
    [persistInSession, sessionStorageKey],
  );

  const handleCollectFingerprint =
    useCallback(async (): Promise<FingerprintCollectionResult> => {
      if (!isSupported) {
        const result: FingerprintCollectionResult = {
          status: FingerprintCollectionStatus.FAILED,
          error: "Fingerprinting not supported in this browser",
          collectionTimeMs: 0,
        };

        setStatus(result.status);
        setError(result.error || null);
        onCollectionError?.(result.error || "Unknown error");

        return result;
      }

      setStatus(FingerprintCollectionStatus.IN_PROGRESS);
      setError(null);

      try {
        const result = await collectFingerprint();

        setStatus(result.status);
        setCollectionTimeMs(result.collectionTimeMs);

        if (
          result.status === FingerprintCollectionStatus.COMPLETED &&
          result.data
        ) {
          setFingerprintData(result.data);
          saveToSession(result.data);
          onCollectionComplete?.(result);
        } else {
          setError(result.error || "Collection failed");
          onCollectionError?.(result.error || "Collection failed");
        }

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        const result: FingerprintCollectionResult = {
          status: FingerprintCollectionStatus.FAILED,
          error: errorMessage,
          collectionTimeMs: 0,
        };

        setStatus(result.status);
        setError(errorMessage);
        onCollectionError?.(errorMessage);

        return result;
      }
    }, [isSupported, saveToSession, onCollectionComplete, onCollectionError]);

  const clearFingerprint = useCallback((): void => {
    setFingerprintData(null);
    setStatus(FingerprintCollectionStatus.NOT_STARTED);
    setError(null);
    setCollectionTimeMs(null);

    if (persistInSession) {
      sessionStorage.removeItem(sessionStorageKey);
    }
  }, [persistInSession, sessionStorageKey]);

  const getFingerprintHash = useCallback((): string | null => {
    return fingerprintData?.hash || null;
  }, [fingerprintData]);

  useEffect(() => {
    const existingFingerprint = loadFromSession();
    if (existingFingerprint) {
      setFingerprintData(existingFingerprint);
      setStatus(FingerprintCollectionStatus.COMPLETED);
    }
  }, [loadFromSession]);

  useEffect(() => {
    if (
      autoCollect &&
      isSupported &&
      status === FingerprintCollectionStatus.NOT_STARTED &&
      !fingerprintData
    ) {
      handleCollectFingerprint();
    }
  }, [
    autoCollect,
    isSupported,
    status,
    fingerprintData,
    handleCollectFingerprint,
  ]);

  return {
    fingerprintData,
    status,
    isSupported,
    isCollecting,
    error,
    collectionTimeMs,
    sessionId,
    collectFingerprint: handleCollectFingerprint,
    clearFingerprint,
    getFingerprintHash,
  };
};

export const useFingerprintHash = (): string | null => {
  const { getFingerprintHash } = useFingerprint({ autoCollect: true });
  return getFingerprintHash();
};

export const useHasFingerprint = (): boolean => {
  const { fingerprintData, status } = useFingerprint({ autoCollect: false });
  return status === FingerprintCollectionStatus.COMPLETED && !!fingerprintData;
};
